import gsap from "gsap";
import EmblaCarousel from "embla-carousel";

const Maze = (element) => {
  // -------------------------------------------------------------------------//
  // -- device detection -- //

  const getDeviceType = () => {
    const viewportWidth = window.innerWidth;

    const ua = navigator.userAgent;

    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
      return "tablet";
    }

    if (viewportWidth >= 768 && viewportWidth <= 1024) {
      return "tablet";
    }

    if (
      /Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(
        ua
      )
    ) {
      return "mobile";
    }

    if (viewportWidth < 768) {
      return "mobile";
    }

    return "desktop";
  };

  const isIOS = () => {
    return (
      [
        "iPad Simulator",
        "iPhone Simulator",
        "iPod Simulator",
        "iPad",
        "iPhone",
        "iPod",
      ].includes(navigator.platform) ||
      // iPad on iOS 13 detection
      (navigator.userAgent.includes("Mac") && "ontouchend" in document)
    );
  };

  const deviceType = getDeviceType();
  const isMobile = deviceType === "mobile";
  const isTablet = deviceType === "tablet";
  const isDesktop = deviceType === "desktop";
  const isIOSDevice = isIOS();

  // -------------------------------------------------------------------------//
  // -- consts -- //

  const nodeData = [
    {
      name: null,
      validMoves: null,
    },
    {
      name: null,
      validMoves: ["sw"],
    },
    {
      name: null,
      validMoves: ["se", "s", "sw"],
    },
    {
      name: null,
      validMoves: ["nw", "sw"],
    },
    {
      name: null,
      validMoves: null,
    },
    {
      name: null,
      validMoves: ["se", "sw"],
    },
    {
      name: null,
      validMoves: ["ne", "sw", "w", "nw"],
    },
    {
      name: null,
      validMoves: ["ne", "se", "sw"],
    },
    {
      name: null,
      validMoves: ["n", "ne", "nw"],
    },
    {
      name: null,
      validMoves: ["sw"],
    },
    {
      name: null,
      validMoves: ["ne", "e", "se", "sw"],
    },
    {
      name: null,
      validMoves: ["ne", "se", "nw"],
    },
    {
      name: null,
      validMoves: ["ne", "se", "sw", "nw"],
    },
    {
      name: null,
      validMoves: ["se", "sw", "nw"],
    },
    {
      name: null,
      validMoves: ["ne", "sw", "w", "nw"],
    },
    {
      name: null,
      validMoves: ["ne"],
    },
    {
      name: null,
      validMoves: ["se", "s", "sw"],
    },
    {
      name: null,
      validMoves: ["ne", "sw", "nw"],
    },
    {
      name: null,
      validMoves: ["ne", "e", "se"],
    },
    {
      name: null,
      validMoves: ["ne", "nw"],
    },
    {
      name: null,
      validMoves: null,
    },
    {
      name: null,
      validMoves: ["ne", "se"],
    },
    {
      name: null,
      validMoves: ["n", "ne", "se", "nw"],
    },
    {
      name: null,
      validMoves: ["nw"],
    },
    {
      name: null,
      validMoves: null,
    },
  ];

  const DIRECTION_OFFSETS = {
    n: { x: 0, y: 20 },
    s: { x: 0, y: -20 },
    e: { x: -20, y: 0 },
    w: { x: 20, y: 0 },
    ne: { x: -10, y: 10 },
    se: { x: -10, y: -10 },
    nw: { x: 10, y: 10 },
    sw: { x: 10, y: -10 },
  };

  const DIRECTION_STRINGS = Object.keys(DIRECTION_OFFSETS);

  const MOVE_VERTICAL = 6;
  const MOVE_HORIZONTAL = 4;
  const MOVE_NE_SW = 5;
  const MOVE_SE_NW = 1;

  // -------------------------------------------------------------------------//
  // -- DOM -- //

  const siteHeader = document.getElementById("main-header");
  const siteContent = document.getElementById("main-content");
  const siteFooter = document.getElementById("main-footer");
  const mobileMenuOpen = document.getElementById("mobileMenuToggle");
  const mobileMenuClose = document.getElementById("mobileMenuClose");

  const mask = document.getElementById("maze-mask");
  const maskVideo = document.getElementById("maze-mask-video");

  const modal = document.getElementById("maze-modal");
  const modalUnderlay = document.getElementById("maze-modal-underlay");
  const modalClose = document.getElementById("maze-modal-close");
  const breadcrumbModalList = document.getElementById("breadcrumb-modal-list");
  const templateModalBreadcrumb = document.getElementById(
    "template-modal-breadcrumb"
  );
  const modalCarouselPrev = document.getElementById(
    "maze-modal-breadcrumb-prev"
  );
  const modalCarouselNext = document.getElementById(
    "maze-modal-breadcrumb-next"
  );

  const customCursor = document.getElementById("custom-cursor");
  const customCursorIcon = customCursor.querySelector(".overlay__cursor__icon");
  const customCursorTooltip = customCursor.querySelector(
    ".overlay__cursor__tooltipContent"
  );

  const map = document.getElementById("maze-map");
  const mapOpen = document.getElementById("maze-map-open");
  const mapOpenTouch = document.getElementById("maze-map-open-touch");
  const mapClose = document.getElementById("maze-map-close");
  const mapButtons = document.querySelectorAll(".overlay__nav__button");

  const breadcrumbHeaderList = document.getElementById(
    "breadcrumb-header-list"
  );
  const breadcrumbHeaderTouchList = document.getElementById(
    "breadcrumb-header-list-touch"
  );
  const templateHeaderBreadcrumb = document.getElementById(
    "template-header-breadcrumb"
  );

  const mazeImageContainer = document.getElementById("maze-image-container");
  const mazeImage = document.getElementById("maze-image");

  const mazeNodesContainer = document.getElementById("maze-node-container");
  const mazeNodes = document.getElementById("maze-nodes");
  const mazeNodeList = document.querySelectorAll(".mazeNode");

  const mazeRestart = document.getElementById("maze-restart");
  const mazeRestartTouch = document.getElementById("maze-restart-touch");
  const mapRestart = document.getElementById("map-modal-restart");
  const mapUnderlay = document.getElementById("maze-map-underlay");

  const navButtons = document.querySelectorAll(".mazeNavButton");
  const touchNavButtons = document.querySelectorAll(".mazeNavTouchButton");

  const modalOpeners = document.querySelectorAll(".mazeNodeButton");
  const modalContent = document.querySelector(".overlay__modal__content");
  const modalTitle = document.getElementById("modal-title");
  const modalBody = document.getElementById("modal-body");

  const navButtonsByDirection = {};
  const touchNavButtonsByDirection = {};

  modalOpeners.forEach((button) => {
    const index = button.getAttribute("data-index");
    const name = button.getAttribute("data-title");
    const body = button.getAttribute("data-body");

    nodeData[index] = {
      ...nodeData[index],
      id: index,
      name: name || null,
      body: body || null,
    };
  });

  navButtons.forEach((button) => {
    navButtonsByDirection[button.getAttribute("data-direction")] = button;
  });
  touchNavButtons.forEach((button) => {
    touchNavButtonsByDirection[button.getAttribute("data-direction")] = button;
  });

  // -------------------------------------------------------------------------//
  // -- state -- //

  let activeNodeId = 12;
  let activeNode = null;
  let adjacentNodes = null;

  let currentPos = { x: 0, y: 0 };
  let modalOpen = false;
  let viewportSize = null;

  let breadcrumbs = [];

  let emblaApi = null;

  // -------------------------------------------------------------------------//
  // -- methods -- //

  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

  const animateMovement = (element, x, y, duration = 0.75) => {
    gsap.killTweensOf(element);
    gsap.to(element, {
      x: `${x}%`,
      y: `${y}%`,
      ease: "power4.out",
      duration,
    });
  };

  const calculateMove = (directionString) => {
    const moveMap = {
      n: -MOVE_VERTICAL,
      ne: -MOVE_NE_SW,
      e: -MOVE_HORIZONTAL,
      se: MOVE_SE_NW,
      s: MOVE_VERTICAL,
      nw: -MOVE_SE_NW,
      w: MOVE_HORIZONTAL,
      sw: MOVE_NE_SW,
    };

    return moveMap[directionString] || 0;
  };

  const assignAdjacentNodes = () => {
    const { validMoves } = activeNode;

    adjacentNodes = {};

    validMoves.forEach((key) => {
      const adjacentNodeId = activeNodeId + calculateMove(key);
      const adjacentNodeData = nodeData[adjacentNodeId];

      adjacentNodes[key] = adjacentNodeData;
    });

    navButtons.forEach((button) => {
      button.classList.add("disabled");
    });
    touchNavButtons.forEach((touchButton) => {
      touchButton.classList.add("disabled");

      const touchButtonContent = touchButton.querySelector(
        ".mazeNavTouchButton__text"
      );
      touchButtonContent.innerHTML = "";
    });

    Object.keys(adjacentNodes).forEach((key) => {
      const button = navButtonsByDirection?.[key];
      const touchButton = touchNavButtonsByDirection?.[key];

      if (!button) {
        console.warn("Nav Button not found for key: ", key);
        return;
      }

      button.classList.remove("disabled");
      touchButton.classList.remove("disabled");

      const touchButtonContent = touchButton.querySelector(
        ".mazeNavTouchButton__text"
      );
      touchButtonContent.innerHTML =
        adjacentNodes?.[key]?.name || "Define your Legacy";
    });
  };

  const updateBreadcrumb = () => {
    breadcrumbHeaderList.innerHTML = "";
    breadcrumbHeaderTouchList.innerHTML = "";
    breadcrumbModalList.innerHTML = "";

    if (!breadcrumbs?.[0]) {
      return;
    }

    breadcrumbs.forEach((crumb) => {
      // Create modal breadcrumb
      const modalClone = templateModalBreadcrumb.content.cloneNode(true);

      const modalTextElement = modalClone.querySelector(
        ".overlay__modal__breadcrumbText"
      );
      modalTextElement.textContent = crumb.name;

      const modalButtonElement = modalClone.querySelector(
        ".overlay__modal__breadcrumbButton"
      );
      modalButtonElement.setAttribute("data-id", crumb.id);

      const originalNode = document.querySelector(`.mazeNode--${crumb.id}`);
      const originalSvg = originalNode?.querySelector(".mazeNode__icon");

      if (originalSvg) {
        const svgClone = originalSvg.cloneNode(true);
        svgClone.classList.remove("mazeNode__icon");
        modalClone
          .querySelector(".overlay__modal__breadcrumbIcon")
          .appendChild(svgClone);
      }

      breadcrumbModalList.appendChild(modalClone);
    });

    emblaApi.reInit();

    const slides = document.querySelectorAll(".embla__slide");

    slides.forEach((slide) => {
      slide.classList.remove("active");
    });

    setActiveSlide();
    goToLastCarouselItem();

    const headerBreadcrumbs = breadcrumbs.slice(-3);

    headerBreadcrumbs.forEach((crumb) => {
      const desktopClone = templateHeaderBreadcrumb.content.cloneNode(true);
      const desktopTextElement = desktopClone.querySelector(
        ".mazeBreadcrumbs__text"
      );

      desktopTextElement.textContent = crumb.name;

      const desktopButtonElement = desktopClone.querySelector(
        ".mazeBreadcrumbs__button"
      );

      desktopButtonElement.setAttribute("data-id", crumb.id);
      desktopButtonElement.addEventListener("click", () => {
        goToNode(crumb.id);
        modal.classList.add("active");
      });

      const touchClone = templateHeaderBreadcrumb.content.cloneNode(true);
      const touchTextElement = touchClone.querySelector(
        ".mazeBreadcrumbs__text"
      );

      touchTextElement.textContent = crumb.name;

      const touchButtonElement = touchClone.querySelector(
        ".mazeBreadcrumbs__button"
      );

      touchButtonElement.setAttribute("data-id", crumb.id);
      touchButtonElement.addEventListener("click", () => {
        goToNode(crumb.id);
        modal.classList.add("active");
      });

      if (breadcrumbHeaderList) {
        breadcrumbHeaderList.appendChild(desktopClone);
      }

      if (breadcrumbHeaderTouchList) {
        breadcrumbHeaderTouchList.appendChild(touchClone);
      }
    });
  };

  const pushBreadcrumb = () => {
    if (
      activeNodeId === 12 ||
      !activeNode ||
      !activeNode?.name ||
      typeof activeNode.name !== "string"
    ) {
      return;
    }

    let { name } = activeNode;

    if (name.includes("\n")) {
      name = name.replace("\n", " ");
    }

    breadcrumbs.push({
      ...activeNode,
      name,
    });

    updateBreadcrumb();
  };

  const setNodeData = () => {
    activeNode = nodeData[activeNodeId];

    pushBreadcrumb();
    assignAdjacentNodes();
  };

  const findPath = (startId, targetId) => {
    const visited = new Set();
    const paths = [];

    const dfs = (currentId, path) => {
      if (currentId === targetId) {
        paths.push([...path]);
        return;
      }

      visited.add(currentId);

      const currentNode = nodeData[currentId];

      if (!currentNode?.validMoves) {
        return;
      }

      currentNode.validMoves.forEach((direction) => {
        const nextId = currentId + calculateMove(direction);

        if (!visited.has(nextId) && nodeData[nextId]) {
          path.push({ id: nextId, direction });

          dfs(nextId, path);

          path.pop();
        }
      });

      visited.delete(currentId);
    };

    dfs(startId, []);
    return paths[0] || [];
  };

  const go = (direction) => {
    if (!activeNode?.validMoves || !activeNode.validMoves.includes(direction)) {
      console.warn(
        "Invalid move from node:",
        activeNodeId,
        "to direction:",
        direction
      );
      return;
    }

    activeNodeId = activeNodeId + calculateMove(direction);
    setNodeData();

    const { x = 0, y = 0 } = DIRECTION_OFFSETS[direction] || {};

    currentPos.x = clamp(currentPos.x + x, -40, 40);
    currentPos.y = clamp(currentPos.y + y, -40, 40);

    let adjustedX = currentPos.x;
    let adjustedY = currentPos.y;

    if (isIOSDevice && (isMobile || isTablet)) {
      adjustedX = currentPos.x * 1.00667;
    }

    animateMovement(mazeImageContainer, adjustedX, adjustedY);
    animateMovement(mazeNodes, currentPos.x, currentPos.y);

    mazeNodeList.forEach((mazeNode) => {
      mazeNode.classList.remove("active");
    });

    navButtons.forEach((button) => {
      const classList = Array.from(button.classList);

      classList.forEach((className) => {
        if (className.startsWith("adjust-")) {
          button.classList.remove(className);
        }
      });

      button.classList.add(`adjust-${activeNodeId}`);
    });

    if (mazeNodeList?.[activeNodeId]) {
      const newlyActiveNode = mazeNodeList[activeNodeId];
      newlyActiveNode.classList.add("active");
      return newlyActiveNode;
    }

    return null;
  };

  const traverseToNode = (targetNodeId, options = {}) => {
    const { delay = 300, duration = 300, onComplete, onNodeReached } = options;

    if (!nodeData[targetNodeId]) {
      console.warn(`Target node ${targetNodeId} does not exist`);
      return;
    }

    const path = findPath(activeNodeId, parseInt(targetNodeId));

    if (path.length === 0) {
      console.warn("No valid path found to target node");
      return;
    }

    return new Promise((resolve) => {
      let currentIndex = 0;

      const animate = () => {
        if (currentIndex >= path.length) {
          // Update breadcrumbs once we reach the target
          const targetIndex = breadcrumbs.findIndex(
            (crumb) => crumb.id === targetNodeId
          );

          if (targetIndex !== -1) {
            breadcrumbs = breadcrumbs.slice(0, targetIndex + 1);
            updateBreadcrumb();
          }

          if (onComplete) onComplete();
          resolve();
          return;
        }

        const { direction } = path[currentIndex];
        const { x = 0, y = 0 } = DIRECTION_OFFSETS[direction] || {};

        currentPos.x = clamp(currentPos.x + x, -40, 40);
        currentPos.y = clamp(currentPos.y + y, -40, 40);

        let adjustedX = currentPos.x;
        let adjustedY = currentPos.y;

        if (isIOSDevice && (isMobile || isTablet)) {
          adjustedX = currentPos.x * 1.00667;
        }

        gsap.killTweensOf(mazeImageContainer);
        gsap.killTweensOf(mazeNodes);

        gsap.to(mazeImageContainer, {
          x: `${adjustedX}%`,
          y: `${adjustedY}%`,
          ease: "power4.out",
          duration: duration / 1000,
          onComplete: () => {
            activeNodeId = activeNodeId + calculateMove(direction);
            setNodeData();

            mazeNodeList.forEach((mazeNode) => {
              mazeNode.classList.remove("active");
            });

            if (mazeNodeList[activeNodeId]) {
              mazeNodeList[activeNodeId].classList.add("active");
            }

            navButtons.forEach((button) => {
              const classList = Array.from(button.classList);
              classList.forEach((className) => {
                if (className.startsWith("adjust-")) {
                  button.classList.remove(className);
                }
              });
              button.classList.add(`adjust-${activeNodeId}`);
            });

            if (onNodeReached) {
              onNodeReached(activeNodeId);
            }

            currentIndex++;
            setTimeout(animate, delay);
          },
        });

        gsap.to(mazeNodes, {
          x: `${currentPos.x}%`,
          y: `${currentPos.y}%`,
          ease: "power4.out",
          duration: duration / 1000,
        });
      };

      animate();
    });
  };

  const goToNode = (targetNodeId) => {
    const path = findPath(activeNodeId, parseInt(targetNodeId));

    if (path.length === 0) {
      console.warn("No valid path found to target node");
      return;
    }

    path.forEach(({ id, direction }) => {
      go(direction);
    });

    const targetIndex = breadcrumbs.findIndex(
      (crumb) => crumb.id === targetNodeId
    );

    if (targetIndex !== -1) {
      breadcrumbs = breadcrumbs.slice(0, targetIndex + 1);
      updateBreadcrumb();
    }
  };

  const goToStart = () => {
    animateMovement(mazeImageContainer, 0, 0);
    animateMovement(mazeNodes, 0, 0);
  };

  const restart = () => {
    mazeNodeList.forEach((mazeNode) => {
      mazeNode.classList.remove("active");
    });

    breadcrumbs = [];
    modalOpen = false;

    breadcrumbHeaderList.innerHTML = "";
    breadcrumbHeaderTouchList.innerHTML = "";

    activeNodeId = 12;
    currentPos = {
      x: 0,
      y: 0,
    };

    goToStart();
    setNodeData();
  };

  // -------------------------------------------------------------------------//
  // -- methods: carousel -- //

  const updateCarouselArrows = () => {
    if (!emblaApi || !modalCarouselPrev || !modalCarouselNext) {
      return;
    }

    const canScrollPrev = emblaApi.canScrollPrev();
    const canScrollNext = emblaApi.canScrollNext();

    modalCarouselPrev.classList.toggle("disabled", !canScrollPrev);
    modalCarouselNext.classList.toggle("disabled", !canScrollNext);
  };

  const setActiveSlide = () => {
    const currentIndex = emblaApi.selectedScrollSnap();
    const slides = document.querySelectorAll(".embla__slide");

    slides.forEach((slide, index) => {
      slide.classList.toggle("active", index === currentIndex);
    });

    const data = getCurrentBreadcrumbData();

    if (data) {
      setModalData(data.title, JSON.parse(data.body));
    }
  };

  const initializeCarousel = () => {
    const emblaNode = document.querySelector(".embla");
    const viewportNode = emblaNode.querySelector(".embla__viewport");

    emblaApi = EmblaCarousel(viewportNode, {
      align: "center",
      containScroll: false,
      speed: 10,
      inViewThreshold: 0.5,
      dragFree: false,
      draggable: false,
    });

    setActiveSlide();
    updateCarouselArrows();

    emblaApi.on("select", setActiveSlide);
  };

  const goToLastCarouselItem = () => {
    const lastIndex = emblaApi.scrollSnapList().length - 1;

    emblaApi.scrollTo(lastIndex, true);

    updateCarouselArrows();
  };

  const getCurrentBreadcrumbData = () => {
    const currentIndex = emblaApi.selectedScrollSnap();
    const currentBreadcrumb = breadcrumbs[currentIndex];

    if (!currentBreadcrumb) {
      return null;
    }

    return {
      title: currentBreadcrumb.name,
      body: currentBreadcrumb.body,
    };
  };

  const breadcrumbPrev = () => {
    if (emblaApi) {
      emblaApi.scrollPrev();

      const data = getCurrentBreadcrumbData();

      if (data) {
        setModalData(data.title, JSON.parse(data.body));
      }

      updateCarouselArrows();
    }
  };

  const breadcrumbNext = () => {
    if (emblaApi) {
      emblaApi.scrollNext();

      const data = getCurrentBreadcrumbData();

      if (data) {
        setModalData(data.title, JSON.parse(data.body));
      }

      updateCarouselArrows();
    }
  };

  // -------------------------------------------------------------------------//
  // -- methods: execution -- //

  const initialize = () => {
    document.body.style.overflow = "hidden";

    if (siteHeader) {
      siteHeader.style.background = "#040707";
    }
    if (siteContent) {
      siteContent.style.position = "relative";
      siteContent.style.pointerEvents = "none";
      siteContent.style.zIndex = "300";
    }
    if (siteFooter) {
      siteFooter.style.display = "none";
    }

    if (maskVideo && mask) {
      maskVideo.addEventListener("ended", () => {
        mask.classList.remove("active");
      });

      setTimeout(() => {
        maskVideo.play();
      }, 1000);

      setTimeout(() => {
        mask.classList.remove("active");
      }, 3000);
    }

    activeNode = nodeData[activeNodeId];
    assignAdjacentNodes();
  };

  const resetCursor = () => {
    if (!customCursor) {
      return;
    }

    customCursor.classList.add("disabled");

    DIRECTION_STRINGS.forEach((directionString) => {
      customCursorIcon.classList.remove(directionString);
    });

    customCursorTooltip.classList.add("disabled");
  };

  const setModalData = (title, body) => {
    modalTitle.innerHTML = title;

    modalBody.classList.add("animating");
    modalBody.classList.remove("active");

    modalBody.innerHTML = body;

    setTimeout(() => {
      modalBody.classList.remove("animating");
    }, 100);

    setTimeout(() => {
      modalBody.classList.add("active");
    }, 300);
  };

  const addClickListeners = () => {
    if (navButtons?.[0]) {
      navButtons.forEach((navButton) => {
        navButton.addEventListener("click", () => {
          const direction = navButton.getAttribute("data-direction");

          go(direction);

          const residualHoverValue = adjacentNodes?.[direction];

          if (residualHoverValue) {
            customCursorTooltip.classList.remove("hidden");
            customCursorTooltip.innerHTML = residualHoverValue.name;
          } else {
            customCursor.classList.add("disabled");
            customCursorTooltip.classList.add("disabled");
          }
        });
      });
    }
    if (touchNavButtons?.[0]) {
      touchNavButtons.forEach((touchNavButton) => {
        touchNavButton.addEventListener("click", () => {
          const direction = touchNavButton.getAttribute("data-direction");

          go(direction);
        });
      });
    }

    if (mapButtons?.[0]) {
      mapButtons.forEach((mapButton) => {
        mapButton.addEventListener("click", () => {
          const index = mapButton.getAttribute("data-index");

          modalOpen = false;
          map.classList.remove("active");

          goToNode(index);
        });
      });
    }

    if (modalOpeners?.[0]) {
      modalOpeners.forEach((modalOpener) => {
        modalOpener.addEventListener("click", () => {
          if (modal) {
            modalOpen = true;

            modal.classList.add("active");

            setModalData(
              modalOpener.getAttribute("data-title"),
              JSON.parse(modalOpener.getAttribute("data-body"))
            );
          }
        });
      });
    }
    if (modalClose) {
      modalClose.addEventListener("click", () => {
        modalOpen = false;
        modal.classList.remove("active");
      });
    }
    if (modalUnderlay) {
      modalUnderlay.addEventListener("click", () => {
        modalOpen = false;
        modal.classList.remove("active");
      });
    }
    if (modalCarouselPrev) {
      modalCarouselPrev.addEventListener("click", () => {
        breadcrumbPrev();
      });
    }
    if (modalCarouselNext) {
      modalCarouselNext.addEventListener("click", () => {
        breadcrumbNext();
      });
    }

    if (mazeRestart) {
      mazeRestart.addEventListener("click", () => {
        restart();
      });
    }
    if (mazeRestartTouch) {
      mazeRestartTouch.addEventListener("click", () => {
        restart();
      });
    }
    if (mapRestart) {
      mapRestart.addEventListener("click", () => {
        restart();

        if (modal) {
          modal.classList.remove("active");
        }
      });
    }

    if (mapOpen) {
      mapOpen.addEventListener("click", () => {
        modalOpen = true;
        map.classList.add("active");
      });
    }
    if (mapOpenTouch) {
      mapOpenTouch.addEventListener("click", () => {
        modalOpen = true;
        map.classList.add("active");
      });
    }
    if (mapClose) {
      mapClose.addEventListener("click", () => {
        modalOpen = false;
        map.classList.remove("active");
      });
    }
    if (mapUnderlay) {
      mapUnderlay.addEventListener("click", () => {
        modalOpen = false;
        map.classList.remove("active");
      });
    }

    if (mobileMenuOpen) {
      mobileMenuOpen.addEventListener("click", () => {
        siteContent.style.zIndex = "0";
      });
    }
    if (mobileMenuClose) {
      mobileMenuClose.addEventListener("click", () => {
        siteContent.style.zIndex = "300";
      });
    }
  };

  const addHoverListeners = () => {
    if (navButtons?.[0]) {
      navButtons.forEach((navButton) => {
        navButton.addEventListener("mouseenter", () => {
          if (!isDesktop) {
            return;
          }

          resetCursor();

          const direction = navButton.getAttribute("data-direction");
          const isDisabled = navButton.classList.contains("disabled");
          const adjacentNode = adjacentNodes?.[direction];

          if (isDisabled || !customCursor) {
            return;
          }

          customCursor.classList.remove("disabled");
          customCursorIcon.classList.add(direction);

          customCursorTooltip.classList.remove("disabled");

          if (adjacentNode?.name) {
            customCursorTooltip.classList.remove("hidden");
            customCursorTooltip.innerHTML = adjacentNode.name;
          } else {
            customCursorTooltip.classList.add("hidden");
            customCursorTooltip.innerHTML = "";
          }
        });

        navButton.addEventListener("mouseleave", () => {
          resetCursor();
        });
      });
    }
  };

  const addKeyListeners = () => {
    window.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        modal?.classList.remove("active");
        map?.classList.remove("active");
        modalOpen = false;
      }
    });
  };

  const addMousemoveListeners = () => {
    const handleMousemove = (e) => {
      if (!isDesktop || viewportSize === null || modalOpen) {
        animateMovement(mazeImage, 0, 0);
        animateMovement(mazeNodesContainer, 0, 0);
        return;
      }

      const { width, height } = viewportSize;

      const mouseX = e.clientX / width;
      const mouseY = e.clientY / height;

      const offsetX = -(mouseX - 0.5) * 0.75;
      const offsetY = -(mouseY - 0.5) * 0.75;

      animateMovement(mazeImage, offsetX, offsetY);
      animateMovement(mazeNodesContainer, offsetX * 0.9, offsetY * 0.9);

      if (customCursor) {
        gsap.killTweensOf(customCursor);
        gsap.to(customCursor, {
          x: `${mouseX * 100}vw`,
          y: `${mouseY * 100}vh`,
          duration: 0,
        });
      }
    };

    window.addEventListener("mousemove", handleMousemove);
  };

  const addResizeListeners = () => {
    const handleResize = (e) => {
      const { innerWidth, innerHeight } = window;

      viewportSize = {
        width: innerWidth,
        height: innerHeight,
      };
    };

    window.addEventListener("resize", handleResize);

    handleResize();
  };

  const addTouchListeners = () => {
    if (modalContent) {
      let startY = 0;
      let currentY = 0;

      modalContent.addEventListener(
        "touchstart",
        (e) => {
          startY = e.touches[0].clientY;
          currentY = modalContent.scrollTop;
        },
        { passive: false }
      );

      modalContent.addEventListener(
        "touchmove",
        (e) => {
          const touch = e.touches[0];
          const dy = touch.clientY - startY;

          if (
            (modalContent.scrollTop <= 0 && dy > 0) ||
            (modalContent.scrollTop + modalContent.clientHeight >=
              modalContent.scrollHeight &&
              dy < 0)
          ) {
            e.preventDefault();
          }

          e.stopPropagation();
        },
        { passive: false }
      );
    }
  };

  // -------------------------------------------------------------------------//

  const main = () => {
    initializeCarousel();

    initialize();

    addClickListeners();
    addHoverListeners();
    addKeyListeners();
    addMousemoveListeners();
    addResizeListeners();
    // addTouchListeners();
  };

  // -------------------------------------------------------------------------//

  main();
};

export default Maze;
